// Next Diamonds — Card text layout
// Three-row card layout:
//   Row 1: Title  [— flex —]  Metal swatches (right side)
//   Row 2: "2 Carat from"  $list (strikethrough)
//   Row 3: $sale.00  USD
//
// Price logic:
//   - Shopify's product.price is the LIST price (no auto-discount applied)
//   - Active sale promo (window.ndPromo.discountPercent) takes X% off at checkout
//   - We show:  list (strikethrough) → list × (1 − pct/100) sale below
//   - If product has a real Shopify compare_at_price > price, use that as the
//     strikethrough instead (real MSRP markdown)
//
// Re-render: watches .product-grid for child changes (AJAX filter/reset
// replaces the grid HTML; we rebuild cards on every update).

(function () {
  'use strict';

  var SWATCH_SIZE_PX = 13;

  function getDiscountPct() {
    if (!window.ndPromo) return 0;
    var n = parseFloat(window.ndPromo.discountPercent);
    return isFinite(n) && n > 0 && n < 100 ? n : 0;
  }

  function fmtUSD(amount) {
    if (!isFinite(amount)) return '';
    return '$' + amount.toLocaleString('en-US', {
      minimumFractionDigits: 2, maximumFractionDigits: 2
    });
  }

  function fmtInt(amount) {
    if (!isFinite(amount)) return '';
    return '$' + Math.round(amount).toLocaleString('en-US');
  }

  function parsePriceFromText(txt) {
    if (!txt) return null;
    var m = String(txt).match(/\$\s?([\d,]+(?:\.\d+)?)/);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isFinite(n) ? n : null;
  }

  // Force size on a single swatch item via inline style.
  function forceSwatchSize(item, size) {
    var px = size + 'px';
    var style = item.style;
    style.setProperty('width', px, 'important');
    style.setProperty('height', px, 'important');
    style.setProperty('min-width', px, 'important');
    style.setProperty('min-height', px, 'important');
    style.setProperty('max-width', px, 'important');
    style.setProperty('max-height', px, 'important');
    style.setProperty('flex', '0 0 ' + px, 'important');
    style.setProperty('padding', '0', 'important');
    style.setProperty('border-radius', '999px', 'important');
    style.setProperty('overflow', 'hidden', 'important');
    style.setProperty('box-sizing', 'border-box', 'important');

    var children = item.querySelectorAll(
      'img, picture, picture img, source, svg, ' +
      '.image-wrap, [class*="image"], [style*="aspect-ratio"]'
    );
    children.forEach(function (child) {
      child.style.setProperty('width', px, 'important');
      child.style.setProperty('height', px, 'important');
      child.style.setProperty('max-width', px, 'important');
      child.style.setProperty('max-height', px, 'important');
      child.style.setProperty('min-width', px, 'important');
      child.style.setProperty('min-height', px, 'important');
      child.style.setProperty('object-fit', 'cover', 'important');
      child.style.setProperty('border-radius', '999px', 'important');
      child.style.setProperty('aspect-ratio', '1 / 1', 'important');
      if (child.tagName === 'IMG' || child.tagName === 'SOURCE') {
        try { child.setAttribute('width', String(size)); } catch (e) {}
        try { child.setAttribute('height', String(size)); } catch (e) {}
      }
    });
  }

  function readPriceAttr(card, name) {
    var raw = card.getAttribute(name);
    if (raw == null || raw === '') return null;
    var n = parseFloat(raw);
    return isFinite(n) && n > 0 ? n : null;
  }

  // ===== Metal swatch deduplication =====
  // Visual categories (one swatch per category, first DOM occurrence wins):
  //   yellow        — any karat of yellow gold, solo
  //   rose          — any karat of rose gold, solo
  //   white         — any karat of white gold OR platinum OR white+platinum
  //                   mix (all look visually identical: silver/white)
  //   yellow-plat   — yellow + platinum two-tone (visually distinct)
  //   rose-plat     — rose + platinum two-tone (visually distinct)
  //
  // Examples:
  //   "14K Yellow Gold"                          → yellow
  //   "18K Yellow Gold"                          → yellow      (dup)
  //   "14K Yellow Gold with Platinum Prongs"     → yellow-plat
  //   "14K Rose Gold"                            → rose
  //   "14K Rose Gold & Platinum Accents"         → rose-plat
  //   "14K White Gold"                           → white
  //   "18K White Gold"                           → white       (dup)
  //   "Platinum"                                 → white       (dup)
  //   "14K White Gold with Platinum Prongs"      → white       (dup)
  function categorizeMetalSwatch(item) {
    var raw = String(item.getAttribute('data-option-item') || '').toLowerCase();
    if (!raw) return null;
    var hasPlat = /\bplat(?:inum|\b)/.test(raw);
    var hasYellow = raw.indexOf('yellow') !== -1;
    var hasRose = raw.indexOf('rose') !== -1;
    var hasWhite = raw.indexOf('white') !== -1;

    // Two-tone with a colored gold + platinum → distinct visual category
    if (hasYellow && hasPlat) return 'yellow-plat';
    if (hasRose && hasPlat) return 'rose-plat';
    // White gold, platinum alone, white+platinum mix → all "white"
    if (hasWhite || hasPlat) return 'white';
    if (hasYellow) return 'yellow';
    if (hasRose) return 'rose';
    return null;
  }

  function dedupMetalSwatches(container) {
    if (!container) return;
    var items = container.querySelectorAll('.product-block-options__item');
    var seen = {};
    items.forEach(function (item) {
      var cat = categorizeMetalSwatch(item);
      if (!cat) return;                            // unknown — leave as-is
      if (seen[cat]) {
        item.classList.add('nd-metal-hidden');     // duplicate → hide
      } else {
        item.classList.remove('nd-metal-hidden');
        seen[cat] = true;
      }
    });
  }

  function build(card) {
    if (!card || card.dataset.ndCardBuilt === '1') return;

    var titleEl = card.querySelector('.product-block__title');
    var priceEl = card.querySelector('.product-price');
    if (!titleEl) return;
    var title = titleEl.textContent.replace(/\s+/g, ' ').trim();
    if (!title) return;

    // Carat
    var carat = card.getAttribute('data-base-carat') || '';
    var caratNum = carat ? parseFloat(carat) : NaN;
    var caratLabel = '';
    if (isFinite(caratNum) && caratNum > 0) {
      var caratStr = (Math.round(caratNum * 100) / 100).toString();
      caratLabel = caratStr + ' Carat';
    }

    // === Price logic ===
    // listPrice  = the price Shopify shows (product.price_min)
    // compareAt  = product.compare_at_price_min (real MSRP if merchant set it)
    // pct        = active sale promo % off at checkout
    //
    // Decide which "original" to strike through, and what "sale" to show.
    var listPrice = readPriceAttr(card, 'data-price-min')
                 || readPriceAttr(card, 'data-price')
                 || (priceEl ? parsePriceFromText(priceEl.textContent) : null);
    var compareAt = readPriceAttr(card, 'data-compare-at-price-min')
                 || readPriceAttr(card, 'data-compare-at-price');
    var pct = getDiscountPct();

    var originalPrice = null;
    var salePrice = listPrice;

    if (compareAt != null && compareAt > listPrice) {
      // Real Shopify markdown — use compare_at as strikethrough
      originalPrice = compareAt;
      salePrice = listPrice;
    } else if (pct > 0 && listPrice != null) {
      // Active sale promo — strike Shopify price, sale = list × (1 − pct/100)
      originalPrice = listPrice;
      salePrice = listPrice * (1 - pct / 100);
    } else {
      // No sale — just show the list price (no strikethrough)
      originalPrice = null;
      salePrice = listPrice;
    }

    // ===== Build the info block =====
    var wrap = document.createElement('div');
    wrap.className = 'nd-card-info';

    // --- Row 1: Title + Swatches ---
    var titleRow = document.createElement('div');
    titleRow.className = 'nd-card-info__title-row';

    var titleSpan = document.createElement('span');
    titleSpan.className = 'nd-card-info__title';
    titleSpan.textContent = title;
    titleSpan.style.setProperty('order', '1', 'important');
    titleRow.appendChild(titleSpan);

    var metalSwatches = card.querySelector('.product-block-options[data-option-name="Metal" i]')
                     || card.querySelector('.product-block-options[data-option-name*="etal" i]');
    if (metalSwatches) {
      metalSwatches.classList.add('nd-card-info__swatches');
      metalSwatches.style.setProperty('order', '2', 'important');
      metalSwatches.style.setProperty('flex', '0 0 auto', 'important');
      metalSwatches.style.setProperty('width', 'auto', 'important');
      metalSwatches.style.setProperty('max-width', 'none', 'important');
      titleRow.appendChild(metalSwatches);
      // Dedup BEFORE forcing size so we don't size swatches we'll hide
      dedupMetalSwatches(metalSwatches);
      var items = metalSwatches.querySelectorAll('.product-block-options__item:not(.nd-metal-hidden)');
      items.forEach(function (it) { forceSwatchSize(it, SWATCH_SIZE_PX); });
    }
    wrap.appendChild(titleRow);

    // --- Row 2: single line — "2 CT • $3,200 → $1,600" ---
    // If no sale (no compare-at, no promo): "2 CT • $1,600"
    // No "USD" suffix, no "from" prefix, no stacking — clean one-liner.
    var line = document.createElement('div');
    line.className = 'nd-card-info__line';

    if (caratLabel) {
      var caratEl = document.createElement('span');
      caratEl.className = 'nd-card-info__ct';
      // Compact "2 CT" instead of "2 Carat" — more minimalist
      var caratCompact = caratLabel.replace(/\s*Carat\s*/i, ' CT');
      caratEl.textContent = caratCompact;
      line.appendChild(caratEl);

      var dot = document.createElement('span');
      dot.className = 'nd-card-info__sep';
      dot.textContent = '•';
      line.appendChild(dot);
    }

    if (originalPrice != null) {
      var was = document.createElement('span');
      was.className = 'nd-card-info__price-was';
      was.textContent = fmtInt(originalPrice);
      line.appendChild(was);

      var arrow = document.createElement('span');
      arrow.className = 'nd-card-info__arrow';
      arrow.textContent = '→';
      line.appendChild(arrow);
    }

    if (salePrice != null) {
      var sale = document.createElement('span');
      sale.className = 'nd-card-info__price-sale';
      sale.textContent = fmtInt(salePrice);
      line.appendChild(sale);
    }

    wrap.appendChild(line);

    var infoContainer = card.querySelector('.info-container') || titleEl.parentNode;
    var existing = infoContainer.querySelector('.nd-card-info');
    if (existing) existing.remove();
    infoContainer.appendChild(wrap);

    card.dataset.ndCardBuilt = '1';
  }

  function buildAll() {
    var cards = document.querySelectorAll('product-block.product-block, .product-block');
    cards.forEach(build);
  }

  function reset() {
    document.querySelectorAll('product-block.product-block, .product-block').forEach(function (c) {
      c.dataset.ndCardBuilt = '';
    });
    buildAll();
  }

  // ====================================================================
  // AJAX RE-RENDER
  // The theme replaces .product-grid contents after filter changes /
  // reset. Watch the grid for child mutations and rebuild cards.
  // Also watch [data-ajax-container] for the .ajax-loading class flipping
  // off — that signals an AJAX refresh just completed.
  // ====================================================================
  function watchForReplacements() {
    var grids = document.querySelectorAll('.product-grid');
    grids.forEach(function (grid) {
      if (grid.dataset.ndGridWatched === '1') return;
      grid.dataset.ndGridWatched = '1';
      var gridObs = new MutationObserver(function () {
        // Debounce — many mutations during AJAX replace
        clearTimeout(grid.__ndRebuildTimer);
        grid.__ndRebuildTimer = setTimeout(reset, 50);
      });
      gridObs.observe(grid, { childList: true, subtree: false });
    });

    var ajaxContainers = document.querySelectorAll('[data-ajax-container]');
    ajaxContainers.forEach(function (el) {
      if (el.dataset.ndAjaxWatched === '1') return;
      el.dataset.ndAjaxWatched = '1';
      var wasLoading = el.classList.contains('ajax-loading');
      var ajaxObs = new MutationObserver(function () {
        var nowLoading = el.classList.contains('ajax-loading');
        if (wasLoading && !nowLoading) {
          // AJAX done — rebuild
          setTimeout(reset, 80);
          setTimeout(watchForReplacements, 100);
        }
        wasLoading = nowLoading;
      });
      ajaxObs.observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // ====================================================================
  // SEARCH OVERLAY RESULTS
  // The header search injects its result cards while the customer types,
  // long after init, and not inside a watched grid. Without this the CSS
  // hides their default title + price and nothing is built in their place
  // (cards showed image, swatches and stars only).
  // ====================================================================
  function watchSearchOverlay() {
    document.querySelectorAll('.main-search__results').forEach(function (box) {
      if (box.dataset.ndSearchWatched === '1') return;
      box.dataset.ndSearchWatched = '1';
      new MutationObserver(function () {
        // build() skips cards it already built, so its own DOM changes
        // settle after one extra pass.
        clearTimeout(box.__ndBuildTimer);
        box.__ndBuildTimer = setTimeout(buildAll, 50);
      }).observe(box, { childList: true, subtree: true });
    });
  }

  function init() {
    buildAll();
    watchForReplacements();
    watchSearchOverlay();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Keep the legacy event listener as a safety net
  document.addEventListener('on:ajax-products:updated', reset);
  // Safety re-runs in case the grid renders asynchronously
  setTimeout(init, 600);
  setTimeout(init, 1600);
})();
